
import axios from "axios";

/*
|--------------------------------------------------------------------------
| Vendor Category API Service
|--------------------------------------------------------------------------
| Handles all API requests for vendor categories.
| Laravel API base URL: /api/vendor-categories
|--------------------------------------------------------------------------
*/

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

const vendorCategoryApi = axios.create({
  baseURL: `${API_BASE_URL.replace(/\/$/, "")}/vendor-categories`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

/*
|--------------------------------------------------------------------------
| Authentication
|--------------------------------------------------------------------------
*/

vendorCategoryApi.interceptors.request.use(
  (config) => {
    const token =
      sessionStorage.getItem("token") ||
      localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/*
|--------------------------------------------------------------------------
| Response Helpers
|--------------------------------------------------------------------------
*/

const handleSuccess = (response, fallbackMessage) => ({
  success: true,
  data: response.data?.data ?? response.data,
  message: response.data?.message || fallbackMessage,
  meta: response.data?.meta || null,
});

const handleError = (error) => ({
  success: false,
  data: null,
  message:
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message ||
    "Something went wrong.",
  errors: error.response?.data?.errors || {},
  status: error.response?.status || null,
});

/*
|--------------------------------------------------------------------------
| Vendor Category CRUD Operations
|--------------------------------------------------------------------------
*/

const vendorCategoryService = {
  // GET: Fetch all vendor categories
  async getAll(params = {}) {
    try {
      const response = await vendorCategoryApi.get("/", { params });

      return handleSuccess(
        response,
        "Vendor categories fetched successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },

  // GET: Fetch one vendor category
  async getById(id) {
    try {
      const response = await vendorCategoryApi.get(
        `/${encodeURIComponent(id)}`
      );

      return handleSuccess(
        response,
        "Vendor category fetched successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },

  // POST: Create a vendor category
  async create(categoryData) {
    try {
      const response = await vendorCategoryApi.post("/", categoryData);

      return handleSuccess(
        response,
        "Vendor category created successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },

  // PUT: Update a vendor category
  async update(id, categoryData) {
    try {
      const response = await vendorCategoryApi.put(
        `/${encodeURIComponent(id)}`,
        categoryData
      );

      return handleSuccess(
        response,
        "Vendor category updated successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },

  // DELETE: Delete a vendor category
  async delete(id) {
    try {
      const response = await vendorCategoryApi.delete(
        `/${encodeURIComponent(id)}`
      );

      return handleSuccess(
        response,
        "Vendor category deleted successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },

  // PATCH: Update category status
  async updateStatus(id, status) {
    try {
      const response = await vendorCategoryApi.patch(
        `/${encodeURIComponent(id)}/status`,
        { status }
      );

      return handleSuccess(
        response,
        "Vendor category status updated successfully."
      );
    } catch (error) {
      return handleError(error);
    }
  },
};

export default vendorCategoryService;
